import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Inbox, RefreshCw, Search, SearchX, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useDraftIndicators } from "@/hooks/inbox/useDraft";
import { ChannelFilter } from "./ChannelFilter";
import { ConversationListItem } from "./ConversationListItem";
import { useInboxUI } from "./InboxProvider";
import { useFilteredConversations } from "./useFilteredConversations";

const ListSkeleton: React.FC = () => (
	<div className="space-y-1 p-3" aria-hidden>
		{Array.from({ length: 7 }).map((_, index) => (
			<div key={index} className="flex items-center gap-3 px-1 py-2">
				<Skeleton className="h-10 w-10 shrink-0 rounded-sm" />
				<div className="flex-1 space-y-2">
					<div className="flex justify-between gap-4">
						<Skeleton className="h-3.5 w-2/5" />
						<Skeleton className="h-3 w-10" />
					</div>
					<Skeleton className="h-3 w-4/5" />
				</div>
			</div>
		))}
	</div>
);

interface ConversationListProps {
	hasActiveAccounts: boolean;
	className?: string;
}

/** Left pane: search, channel filter and the scrollable conversation list. */
export const ConversationList: React.FC<ConversationListProps> = ({
	hasActiveAccounts,
	className,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { selectedId, setSelectedId, channelFilter, setChannelFilter, search, setSearch } =
		useInboxUI();
	const { conversations: filtered, allConversations, isLoading, isFetching, refetch } =
		useFilteredConversations(hasActiveAccounts);
	const draftIds = useDraftIndicators();

	const hasConversations = allConversations.length > 0;

	return (
		<div className={cn("flex h-full min-h-0 flex-col bg-background", className)}>
			<div className="shrink-0 space-y-2 border-b border-border p-3">
				<div className="flex items-center gap-2">
					<div className="relative flex-1">
						<Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							placeholder={t("inbox.search", "Search conversations…")}
							className="h-9 pl-8"
							aria-label={t("inbox.search", "Search conversations…")}
						/>
					</div>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-9 w-9 shrink-0 text-muted-foreground"
								onClick={() => refetch()}
								aria-label={t("inbox.refresh", "Refresh")}
							>
								<RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="bottom">{t("inbox.refresh", "Refresh")}</TooltipContent>
					</Tooltip>
				</div>
				<ChannelFilter value={channelFilter} onChange={setChannelFilter} />
			</div>

			<div className="relative min-h-0 flex-1">
				<div className="absolute inset-0 overflow-y-auto scrollbar-thin">
				{!hasActiveAccounts ? (
					<div className="flex h-full flex-col items-center justify-center p-6">
						<EmptyState
							icon={Inbox}
							title={t("inbox.empty.noAccountsTitle", "Connect your accounts")}
							description={t(
								"inbox.empty.noAccountsDescription",
								"Link LinkedIn, email or WhatsApp to see all your conversations in one place."
							)}
						/>
						<Button
							variant="outline"
							size="sm"
							className="mt-2 gap-2"
							onClick={() => navigate("/settings?tab=integrations")}
						>
							<Settings className="h-4 w-4" />
							{t("inbox.empty.goToIntegrations", "Go to integrations")}
						</Button>
					</div>
				) : isLoading ? (
					<ListSkeleton />
				) : filtered.length === 0 ? (
					<EmptyState
						icon={hasConversations ? SearchX : Inbox}
						title={
							hasConversations
								? t("inbox.empty.noResultsTitle", "No matching conversations")
								: t("inbox.empty.noConversationsTitle", "No conversations yet")
						}
						description={
							hasConversations
								? t("inbox.empty.noResultsDescription", "Try a different search or channel filter.")
								: t(
										"inbox.empty.noConversationsDescription",
										"New messages from your connected accounts will appear here."
									)
						}
						className="h-full"
					/>
				) : (
					<ul role="listbox" aria-label={t("inbox.title", "Inbox")} className="divide-y divide-border/60">
						{filtered.map((conversation) => (
							<ConversationListItem
								key={conversation.id}
								conversation={conversation}
								selected={conversation.id === selectedId}
								hasDraft={draftIds.has(conversation.id)}
								onSelect={setSelectedId}
							/>
						))}
					</ul>
				)}
				</div>
			</div>
		</div>
	);
};
